import React from "react";

import { ClientFactory } from "@/main/common/ClientFactory";
import type { ProfileVisitorInsights as ProfileVisitorInsightsPayload } from "@/main/common/ProfileVisitorInsightsTypes";
import { ProfileCollapsibleSection } from "@/main/react/user/ProfileCollapsibleSection";
import type { Localizer } from "@/main/react/Types";
import logger from "@/lib/logger";

/**
 * The owner-only "who is finding you" block (slice P12), rendered into `UserHeader`'s
 * `visitorInsightSlot`.
 *
 * ## Three things about this component are decisions, not style
 *
 * **1. An aggregate, never a name.** The payload carries one integer and nothing that could identify a
 * visitor. Showing an owner *who* looked at their profile is a separate privacy decision that has not
 * been taken, and a "recent visitors" list is exactly the shape that would arrive by accident if this
 * block ever grew a row per person.
 *
 * **2. The number is its own element, beside the caption — never inside a sentence.** That is a
 * translation decision (slice design §6.3): `profile.visitors.week` is a caption, so none of the 24
 * locales needs a plural branch, and the four plural-rule families this product spans (Slavic alone has
 * three or four forms) never enter the picture. Interpolating the count into the string would be a
 * one-line "improvement" that quietly obliges 24 catalogs to grow plural handling.
 *
 * **3. Zero is an invitation, not a rendered `0`.** §4 rule 5 of the design language bans displaying a
 * negative, and the most-looked-at part of a quiet member's profile is the worst place to break it.
 *
 * ## What it does NOT do
 *
 * No store, no `useQuery`, no polling — a single fetch into `useState`, so AGENTS.md rule 31 has no
 * snapshot to churn and this component cannot contribute to the header-crash class. It renders `null`
 * while loading and on failure, which makes {@link ProfileCollapsibleSection} render nothing at all
 * rather than an empty shell; there is no server-rendered content underneath it to subtract from
 * (rule 32), because the block is owner-only and never reaches a crawler.
 *
 * The caller decides whether this member is the owner and simply does not render the component
 * otherwise. That check is a convenience, not the boundary: the endpoint answers **403** to a non-owner,
 * and that is what the integration test pins.
 */
export function ProfileVisitorInsights(props: { username: string; localizer: Localizer }) {
    const { username, localizer } = props;
    const [insights, setInsights] = React.useState<ProfileVisitorInsightsPayload | null>(null);

    React.useEffect(() => {
        if (!username) {
            return;
        }

        let cancelled = false;
        ClientFactory.getUserVisitorInsights(username)
            .then((response) => {
                if (!cancelled) {
                    setInsights(response.data ?? null);
                }
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                // Owner-only decoration: a failure hides the block and nothing else on the profile
                // changes. Logged rather than swallowed silently so it is visible in client logs.
                logger.error("[ProfileVisitorInsights] Failed loading visitor insights", { error, username });
                setInsights(null);
            });

        return () => {
            cancelled = true;
        };
    }, [username]);

    const visitors = insights?.visitorsThisWeek;

    return (
        <ProfileCollapsibleSection
            title={localizer("profile.visitors.title")}
            localizer={localizer}
            dataPlaywrightId="profile-visitor-insights"
        >
            {typeof visitors === "number" ? (
                <div className="flex flex-col gap-2">
                    {visitors > 0 ? (
                        <div className="flex items-baseline gap-2">
                            {/* The number and the caption are two elements on purpose — see the class
                                docs. Do not merge them into one localized sentence. */}
                            <span
                                className="text-2xl font-semibold tracking-tight text-foreground"
                                data-playwright-id="profile-visitor-insights-count"
                            >
                                {visitors}
                            </span>
                            <span className="text-sm text-muted-foreground">
                                {localizer("profile.visitors.week")}
                            </span>
                        </div>
                    ) : (
                        <p
                            className="text-sm leading-6 text-muted-foreground"
                            data-playwright-id="profile-visitor-insights-empty"
                        >
                            {localizer("profile.visitors.empty")}
                        </p>
                    )}

                    <p className="text-xs text-neutral-text" data-playwright-id="profile-visitor-insights-private">
                        {localizer("profile.visitors.private")}
                    </p>
                </div>
            ) : null}
        </ProfileCollapsibleSection>
    );
}

export default ProfileVisitorInsights;
